import { Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui";
import { useTenantUserSearch } from "~/features/admin/tenants/hooks";
import type { FilterUserOption, TenantMember } from "~/features/admin/tenants/types";

interface AddMembersDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  currentMembers: TenantMember[];
  processing: boolean;
  onSubmit: (userIds: string[]) => void;
}

/** Search the organization's users and add a selection of them as tenant members. */
export function AddMembersDialog({
  open,
  onClose,
  organizationId,
  currentMembers,
  processing,
  onSubmit,
}: AddMembersDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<FilterUserOption[]>([]);

  const currentMemberIds = useMemo(() => currentMembers.map((m) => m.id), [currentMembers]);
  const excludeIds = useMemo(() => [...currentMemberIds, ...selected.map((u) => u.id)], [currentMemberIds, selected]);
  const { options, loading, search, reset } = useTenantUserSearch({
    organizationId,
    excludeIds,
  });

  useEffect(() => {
    if (open) {
      setSelected([]);
      reset();
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">{t("ui.addX", { x: t("model.user.modelName") })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selected.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setSelected((prev) => prev.filter((s) => s.id !== u.id))}
                  className="inline-flex items-center gap-1 rounded-pill bg-primary-50 px-2 py-0.5 text-12-regular text-primary-700 hover:bg-primary-100"
                >
                  <span className="max-w-[200px] truncate">{u.displayName ?? u.email}</span>
                  <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}

          <Command shouldFilter={false} className="rounded-md border border-border-default">
            <CommandInput placeholder={t("ui.search")} onValueChange={search} />
            <CommandList>
              {!loading && <CommandEmpty>{t("feature.tenants.noUsersFound")}</CommandEmpty>}
              {options
                .filter((option) => !selected.some((s) => s.id === option.id))
                .map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    onSelect={() => setSelected((prev) => [...prev, option])}
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-fg-1">{option.displayName ?? option.email}</span>
                        {option.deactivatedAt && <Badge tone="default">{t("ui.deactivated")}</Badge>}
                      </span>
                      {option.displayName && <span className="truncate text-12-regular text-fg-3">{option.email}</span>}
                    </span>
                  </CommandItem>
                ))}
            </CommandList>
          </Command>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={processing}>
            {t("ui.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => onSubmit(selected.map((u) => u.id))}
            disabled={processing || selected.length === 0}
          >
            {processing && <Loader2 className="animate-spin" />}
            <span>{t("ui.save")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
